/**
 * Wrapper del SDK Web de OneSignal (v16).
 *
 * Patron recomendado por la documentacion oficial: "Custom Code Service Worker".
 *   - Cargamos el SDK via script defer.
 *   - Usamos la cola `OneSignalDeferred` para encolar llamadas antes de
 *     que el script termine de cargar (race-safe).
 *   - Llamamos OneSignal.init() DENTRO del callback de la cola.
 *
 * IMPORTANTE: NO pasamos `serviceWorkerPath` ni `serviceWorkerParam`.
 * La app ya tiene su propio SW en /sw.js que controla scope '/'. OneSignal
 * detecta que existe un SW controlando el scope raiz y le entrega los push
 * directamente (modo "Custom Code"). El handler `push` vive en /sw.js.
 *
 * Si manana cambiamos de proveedor, este archivo es el unico que hay
 * que tocar.
 */

const SDK_URL = '/js/OneSignalSDK.page.es6.js';

let initPromise = null;

function isLocalhost() {
    const h = window.location.hostname;
    return h === 'localhost' || h === '127.0.0.1' || h === '::1';
}

/**
 * OneSignal restringe a nivel de panel los hostnames donde la app puede
 * inicializar. Si el hostname actual no es un subdominio de un tenant
 * conocido (sakbekidz.nidoescolar.com, etc.) ni localhost, evitamos la
 * llamada init() para no llenar la consola de warnings en demo /
 * nidoescolar.com raiz.
 */
function isAllowedHost() {
    const h = window.location.hostname;
    if (isLocalhost()) return true;
    if (h === 'nidoescolar.com' || h === 'www.nidoescolar.com') return false;
    if (h.endsWith('.nidoescolar.com')) return true;
    return false;
}

function ensureSdkLoaded() {
    if (window.OneSignal) {
        return Promise.resolve(window.OneSignal);
    }

    window.OneSignalDeferred = window.OneSignalDeferred || [];

    if (! document.querySelector(`script[src="${SDK_URL}"]`)) {
        const script = document.createElement('script');
        script.src = SDK_URL;
        script.defer = true;
        document.head.appendChild(script);
    }

    return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
            reject(new Error('OneSignal SDK no se cargo en 10s.'));
        }, 10000);

        window.OneSignalDeferred.push(async (OneSignal) => {
            clearTimeout(timeout);
            resolve(OneSignal);
        });
    });
}

export async function initOneSignal({ appId, externalUserId }) {
    if (! appId) {
        console.info('[OneSignal] app_id ausente - push deshabilitado.');
        return null;
    }

    if (! isAllowedHost()) {
        // El SDK de OneSignal rechazaria init() con un error de hostname;
        // evitamos el intento y el warning en consola para el dominio raiz.
        console.info('[OneSignal] host no permitido por el panel de OneSignal - push deshabilitado.');
        return null;
    }

    if (initPromise) {
        return initPromise;
    }

    initPromise = (async () => {
        try {
            const OneSignal = await ensureSdkLoaded();

            if (typeof OneSignal.isInitialized === 'function' && OneSignal.isInitialized()) {
                return OneSignal;
            }

            await OneSignal.init({
                appId,
                ...(externalUserId ? { externalUserId: String(externalUserId) } : {}),
                promptOptions: {
                    slidedown: { enabled: false },
                },
                // Sin serviceWorkerPath: OneSignal usara el SW que ya controla '/'.
                // Nuestro /sw.js tiene el handler `push` y `notificationclick`.
                // Para localhost/127.0.0.1 sin TLS: el navegador los trata
                // como origen seguro y OneSignal lo necesita explicito.
                allowLocalhostAsSecureOrigin: isLocalhost(),
            });

            return OneSignal;
        } catch (e) {
            if (e?.message && e.message.includes('already initialized')) {
                return window.OneSignal;
            }
            // Errores de hostname/dominio son esperados en demo; los demas
            // si son interesantes para diagnosticar.
            const isHostnameError = e?.message?.toLowerCase().includes('can only be used on');
            if (! isHostnameError) {
                console.warn('[OneSignal] init fallo:', e?.message);
            } else {
                console.info('[OneSignal] init omitido por hostname:', e.message);
            }
            initPromise = null;
            throw e;
        }
    })();

    return initPromise;
}

export async function requestNotificationPermission() {
    if (! window.OneSignal) return false;
    try {
        return await window.OneSignal.Notifications.requestPermission();
    } catch (e) {
        console.warn('[OneSignal] requestPermission fallo:', e?.message);
        return false;
    }
}

export async function getPlayerId() {
    if (! window.OneSignal) return null;
    try {
        const id = await window.OneSignal.User?.PushSubscription?.id;
        return id || null;
    } catch (e) {
        console.warn('[OneSignal] getPlayerId fallo:', e?.message);
        return null;
    }
}

/**
 * Verifica si el navegador soporta push. Se llama DESPUES de init().
 *
 * Nota importante: `isPushSupported` puede retornar false en localhost
 * aunque el SDK haya inicializado correctamente (porque el navegador aun
 * no ha concedido permisos). Por eso solo lo usamos como "guard" final,
 * y siempre intentamos pedir permiso si el SDK cargo.
 */
export async function isPushSupported() {
    if (! window.OneSignal) return false;
    try {
        return await window.OneSignal.Notifications.isPushSupported();
    } catch {
        return false;
    }
}

/**
 * Verifica si podemos pedir permiso de notificaciones ahora.
 * Mas permisivo que isPushSupported: si el SDK cargo, asumimos que si.
 */
export function canRequestPermission() {
    if (! window.OneSignal) return false;
    if (! ('Notification' in window)) return false;
    return true;
}

export function onSubscriptionChange(callback) {
    if (! window.OneSignal || ! window.OneSignal.User?.PushSubscription) {
        return () => {};
    }
    const handler = (event) => {
        callback({
            playerId: event?.current?.id,
            optedIn: event?.current?.optedIn,
        });
    };
    window.OneSignal.User.PushSubscription.addEventListener('change', handler);

    return () => {
        if (! window.OneSignal || ! window.OneSignal.User?.PushSubscription) return;
        window.OneSignal.User.PushSubscription.removeEventListener('change', handler);
    };
}
